<template>
  <div class="sales-by-category">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-shape-outline"></i> Sales by Category Analysis
            </h2>
            <p class="text-white opacity-75">Comprehensive category performance analysis and insights</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Analysis Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
            <el-date-picker
                  v-model="filters.dateRange"
              type="daterange"
                  range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
            />
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Categories">
            <el-select
                  v-model="filters.category_ids"
              multiple
                  filterable
                  placeholder="All Categories"
                  @change="loadData"
            >
              <el-option
                    v-for="category in categories"
                    :key="category.id"
                    :label="category.name"
                    :value="category.id"
                  />
            </el-select>
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employee">
            <el-select
                  v-model="filters.user_id"
              multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadData"
            >
              <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
            </el-select>
          </el-form-item>
        </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Method">
            <el-select
                  v-model="filters.payment_method_id"
              multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadData"
            >
              <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
            </el-select>
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Analysis Type">
              <el-select
                  v-model="filters.analysis_type"
                  placeholder="Select Analysis"
                  @change="loadData"
                >
                  <el-option label="Revenue Analysis" value="revenue"></el-option>
                  <el-option label="Quantity Analysis" value="quantity"></el-option>
                  <el-option label="Profit Analysis" value="profit"></el-option>
                  <el-option label="Growth Analysis" value="growth"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadData" :loading="loading">
                  Analyze
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading category analysis...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Summary Statistics -->
        <el-row :gutter="20" class="m-b-20">
          <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
            <el-card :body-style="{ padding: '20px' }" class="stat-card">
              <div class="stat-content">
                <div class="stat-info">
                  <h4 class="stat-title">{{ stat.title }}</h4>
                  <h2 class="stat-value">{{ stat.value }}</h2>
                  <div class="stat-comparison">
                    <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                      <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(stat.trend) }}%
                    </span>
                    <span class="comparison-text">vs previous period</span>
                  </div>
                </div>
                <div class="stat-icon">
                  <i :class="stat.icon"></i>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Category Performance Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Category Performance</h4>
                <el-radio-group v-model="chartView" size="mini" @change="updateChartData">
                  <el-radio-button label="revenue">Revenue</el-radio-button>
                  <el-radio-button label="quantity">Quantity</el-radio-button>
                  <el-radio-button label="profit">Profit</el-radio-button>
                </el-radio-group>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="categoryData.length > 0"
                  :chart-data="categoryChartData"
                  :chart-options="categoryChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-bar" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Category Distribution Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Category Distribution</h4>
                <el-switch v-model="showPercentage" active-text="%" inactive-text="Value"/>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="categoryData.length > 0"
                  :chart-data="distributionChartData"
                  :chart-options="distributionChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-pie" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Trends and Comparison -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Category Trends -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Category Trends</h4>
                <el-select v-model="trendPeriod" size="mini" @change="loadTrendsData">
                  <el-option label="Daily" value="daily"></el-option>
                  <el-option label="Weekly" value="weekly"></el-option>
                  <el-option label="Monthly" value="monthly"></el-option>
              </el-select>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No trends data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Category Comparison -->
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-areaspline"></i> Performance Comparison</h4>
              </div>
              <div class="comparison-list">
                <div
                  v-for="(category, index) in topCategories"
                  :key="category.id"
                  class="comparison-item"
                >
                  <div class="category-rank">{{ index + 1 }}</div>
                  <div class="category-info">
                    <div class="category-name">{{ category.name }}</div>
                    <div class="category-stats">
                      <span class="revenue">{{ formatCurrency(category.total_revenue) }}</span>
                      <span class="quantity">{{ category.total_quantity }} units</span>
                    </div>
                    <el-progress
                      :percentage="(category.total_revenue / Math.max(...topCategories.map(c => c.total_revenue))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="category-trend">
                    <span :class="['growth', category.growth > 0 ? 'positive' : 'negative']">
                      <i :class="category.growth > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(category.growth) }}%
                    </span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Detailed Category Analysis -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Detailed Category Analysis</h4>
            <div class="header-actions">
              <el-button-group>
                <el-button size="mini" @click="toggleViewMode">
                  <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                  {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="viewMode === 'table'">
            <el-table
              :data="categoryData"
              style="width: 100%"
              size="small"
              @sort-change="handleSortChange"
            >
              <el-table-column prop="name" label="Category" sortable="custom" />
              <el-table-column label="Revenue" width="150" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.total_revenue) }}
                </template>
              </el-table-column>
              <el-table-column label="Quantity" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_quantity }} units
                </template>
              </el-table-column>
              <el-table-column label="Orders" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_orders }}
                </template>
              </el-table-column>
              <el-table-column label="Avg Order Value" width="150" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.average_order_value) }}
                </template>
              </el-table-column>
              <el-table-column label="Growth" width="120" sortable="custom">
                <template slot-scope="scope">
                  <span :class="['growth', scope.row.growth > 0 ? 'positive' : 'negative']">
                    <i :class="scope.row.growth > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(scope.row.growth) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Market Share" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.market_share }}%
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-view"
                      @click="viewCategoryDetails(scope.row)"
                    />
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-document"
                      @click="exportCategoryReport(scope.row)"
                    />
                  </el-button-group>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Grid View -->
          <div v-else class="grid-view">
            <el-row :gutter="20">
              <el-col
                v-for="category in categoryData"
                :key="category.id"
                :xs="24"
                :sm="12"
                :md="8"
                :lg="6"
                class="m-b-20"
              >
                <el-card :body-style="{ padding: '15px' }" class="category-card">
                  <div class="category-header">
                    <h4 class="category-name">{{ category.name }}</h4>
                    <el-tag :type="getCategoryStatusType(category)" size="mini">
                      {{ getCategoryStatusText(category) }}
                    </el-tag>
                  </div>

                  <div class="category-stats">
                    <div class="stat-row">
                      <span class="label">Revenue:</span>
                      <span class="value">{{ formatCurrency(category.total_revenue) }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="label">Quantity:</span>
                      <span class="value">{{ category.total_quantity }} units</span>
                    </div>
                    <div class="stat-row">
                      <span class="label">Orders:</span>
                      <span class="value">{{ category.total_orders }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="label">Growth:</span>
                      <span :class="['value', 'growth', category.growth > 0 ? 'positive' : 'negative']">
                        {{ category.growth > 0 ? '+' : '' }}{{ category.growth }}%
                      </span>
                    </div>
                  </div>

                  <div class="category-footer">
                    <span class="market-share">{{ category.market_share }}% market share</span>
                    <div class="actions">
                      <el-button size="mini" type="primary" icon="el-icon-view" @click="viewCategoryDetails(category)" />
                      <el-button size="mini" type="success" icon="el-icon-document" @click="exportCategoryReport(category)" />
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Category Details Dialog -->
    <el-dialog
      title="Category Details"
      :visible.sync="categoryDetailsVisible"
      width="80%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedCategory" class="category-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Category Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Category Name">{{ selectedCategory.name }}</el-descriptions-item>
              <el-descriptions-item label="Total Revenue">{{ formatCurrency(selectedCategory.total_revenue) }}</el-descriptions-item>
              <el-descriptions-item label="Total Quantity">{{ selectedCategory.total_quantity }} units</el-descriptions-item>
              <el-descriptions-item label="Total Orders">{{ selectedCategory.total_orders }}</el-descriptions-item>
              <el-descriptions-item label="Average Order Value">{{ formatCurrency(selectedCategory.average_order_value) }}</el-descriptions-item>
              <el-descriptions-item label="Growth Rate">
                <span :class="['growth', selectedCategory.growth > 0 ? 'positive' : 'negative']">
                  {{ selectedCategory.growth > 0 ? '+' : '' }}{{ selectedCategory.growth }}%
                </span>
              </el-descriptions-item>
              <el-descriptions-item label="Market Share">{{ selectedCategory.market_share }}%</el-descriptions-item>
            </el-descriptions>
          </el-col>

          <el-col :span="12">
            <h4>Performance Metrics</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Revenue per Item">{{ formatCurrency(selectedCategory.revenue_per_item) }}</el-descriptions-item>
              <el-descriptions-item label="Items per Order">{{ selectedCategory.items_per_order }}</el-descriptions-item>
              <el-descriptions-item label="Total Items Sold">{{ selectedCategory.total_items_sold }}</el-descriptions-item>
              <el-descriptions-item label="Unique Items">{{ selectedCategory.unique_items }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
      </el-row>

        <el-divider content-position="left">Top Items in Category</el-divider>

        <el-table :data="selectedCategory.top_items || []" size="small">
          <el-table-column prop="name" label="Item Name" />
          <el-table-column prop="quantity" label="Quantity Sold" width="120" />
          <el-table-column prop="revenue" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.revenue) }}
            </template>
          </el-table-column>
          <el-table-column prop="percentage" label="% of Category" width="120">
            <template slot-scope="scope">
              {{ scope.row.percentage }}%
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="categoryDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="exportCategoryReport(selectedCategory)">Export Report</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'SalesByCategory',
  components: {
    BarChart,
    DoughnutChart,
    LineChart
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      categoryDetailsVisible: false,
      selectedCategory: null,
      chartView: 'revenue',
      showPercentage: false,
      trendPeriod: 'monthly',
      categoryData: [],
      trendsData: [],
      trendsPagination: null,
      categoryPagination: null,
      categories: [],
      users: [],
      paymentMethods: [],
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        category_ids: [],
        user_id: [],
        payment_method_id: [],
        analysis_type: 'revenue'
      },
      dateShortcuts: [{
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'Last 3 months',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 3)
          return [start, end]
        }
      }, {
        text: 'This year',
        value: () => {
          const end = new Date()
          const start = new Date(new Date().getFullYear(), 0, 1)
          return [start, end]
        }
      }]
    }
  },
  computed: {
    summaryStats () {
      const totalRevenue = this.categoryData.reduce((sum, cat) => sum + cat.total_revenue, 0)
      const totalQuantity = this.categoryData.reduce((sum, cat) => sum + cat.total_quantity, 0)
      const totalOrders = this.categoryData.reduce((sum, cat) => sum + cat.total_orders, 0)
      const avgGrowth = this.categoryData.length > 0
        ? this.categoryData.reduce((sum, cat) => sum + cat.growth, 0) / this.categoryData.length
        : 0

      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Quantity',
          value: totalQuantity.toLocaleString(),
          trend: 8.3,
          icon: 'mdi mdi-package-variant'
        },
        {
          title: 'Total Orders',
          value: totalOrders.toLocaleString(),
          trend: 5.2,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Growth',
          value: avgGrowth.toFixed(1) + '%',
          trend: avgGrowth,
          icon: 'mdi mdi-trending-up'
        }
      ]
    },
    topCategories () {
      return this.categoryData.slice(0, 5)
    },
    categoryChartData () {
      const labels = this.categoryData.map(cat => cat.name)
      const data = this.categoryData.map(cat => {
        switch (this.chartView) {
          case 'revenue':
            return cat.total_revenue
          case 'quantity':
            return cat.total_quantity
          case 'profit':
            return cat.total_profit || cat.total_revenue * 0.3 // Fallback if profit not available
          default:
            return cat.total_revenue
        }
      })

      return {
        labels: labels,
        datasets: [{
          label: this.chartView.charAt(0).toUpperCase() + this.chartView.slice(1),
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: this.getChartColors(data.length),
          borderWidth: 1
        }]
      }
    },
    categoryChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (this.chartView === 'revenue' || this.chartView === 'profit') {
                  return this.formatCurrency(value)
                }
                return value.toLocaleString()
              }.bind(this)
            }
          }
        }
      }
    },
    distributionChartData () {
      const labels = this.categoryData.map(cat => cat.name)
      const data = this.categoryData.map(cat => {
        if (this.showPercentage) {
          const total = this.categoryData.reduce((sum, c) => sum + c.total_revenue, 0)
          return ((cat.total_revenue / total) * 100).toFixed(1)
        }
        return cat.total_revenue
      })

      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },
    distributionChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => this.formatDate(item.date)),
        datasets: this.categoryData.slice(0, 5).map((category, index) => ({
          label: category.name,
          data: this.trendsData.map(item =>
            item.categories?.find(c => c.category_id === category.id)?.revenue || 0
          ),
          borderColor: this.getChartColors(5)[index],
          backgroundColor: this.getChartColors(5)[index] + '20',
          tension: 0.4
        }))
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top'
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.loadData()
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        category_ids: [],
        user_id: [],
        payment_method_id: [],
        analysis_type: 'revenue'
      }
      this.loadData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadCategories(),
          this.loadUsers(),
          this.loadPaymentMethods()
        ])
        await this.loadData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadCategoryData(),
          this.loadTrendsData()
        ])
      } catch (error) {
        this.handleError(error, 'Failed to load category data')
      } finally {
        this.loading = false
      }
    },
    async loadCategoryData () {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          per_page: 50, // Default to 50 categories per page
          page: 1
        }

        const response = await this.$reportService.getSalesByCategory(params)
        if (response.status === 'success') {
          this.categoryData = response.data
          // Store pagination info if needed
          if (response.pagination) {
            this.categoryPagination = response.pagination
          }
        }
      } catch (error) {
        console.error('Error loading category data:', error)
      }
    },
    async loadTrendsData () {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          period: this.trendPeriod,
          per_page: 1000, // Request more data per page for trends
          page: 1
        }

        const response = await this.$reportService.getCategoryTrends(params)
        if (response.status === 'success') {
          this.trendsData = response.data
          // Store pagination info if needed
          if (response.pagination) {
            this.trendsPagination = response.pagination
          }
        }
      } catch (error) {
        console.error('Error loading trends data:', error)
      }
    },
    async loadCategories () {
      try {
        this.categories = await this.$categoryService.getCategories()
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'pdf'
        }

        const response = await this.$reportService.getSalesByCategory(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    updateChartData () {
      // Chart data will be updated automatically through computed properties
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewCategoryDetails (category) {
      this.selectedCategory = category
      this.categoryDetailsVisible = true
    },
    exportCategoryReport (category) {
      // Implement category-specific report export
      console.log('Exporting report for category:', category.name)
    },
    handleCloseDialog () {
      this.categoryDetailsVisible = false
      this.selectedCategory = null
    },
    getCategoryStatusType (category) {
      if (category.growth > 10) return 'success'
      if (category.growth > 0) return 'warning'
      return 'danger'
    },
    getCategoryStatusText (category) {
      if (category.growth > 10) return 'High Growth'
      if (category.growth > 0) return 'Growing'
      return 'Declining'
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },
    getChartColors (count) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#9C27B0', '#FF9800', '#795548', '#607D8B', '#FF5722'
      ]
      return colors.slice(0, count)
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.sales-by-category {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info {
  flex: 1;
}

.stat-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-container {
  position: relative;
  height: 300px;
}

.comparison-list {
  max-height: 300px;
  overflow-y: auto;
}

.comparison-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.comparison-item:last-child {
  border-bottom: none;
}

.category-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.category-info {
  flex: 1;
  margin-right: 12px;
}

.category-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.category-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.category-trend {
  text-align: right;
  font-size: 12px;
}

.growth {
  font-weight: bold;
}

.growth.positive {
  color: #67C23A;
}

.growth.negative {
  color: #F56C6C;
}

.grid-view {
  margin-top: 20px;
}

.category-card {
  transition: all 0.3s ease;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.category-name {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.category-stats {
  margin-bottom: 15px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.label {
  font-size: 12px;
  color: #909399;
}

.value {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
}

.category-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
}

.market-share {
  font-size: 12px;
  color: #909399;
}

.actions {
  display: flex;
  gap: 5px;
}

.text-muted {
  color: #909399;
}

.p-40 {
  padding: 40px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.m-b-30 {
  margin-bottom: 30px;
}

.p-b-30 {
  padding-bottom: 30px;
}

.p-t-30 {
  padding-top: 30px;
}
</style>
